import { useMemo, useState } from 'react'
import type { ProjectSummary } from '../lib/db'
import {
  awayOn,
  loadCrew,
  newMember,
  NOT_ON_THE_ROSTER,
  saveCrew,
  validAway,
  type Away,
  type CrewMember,
} from '../lib/crew'
import { Empty, NotKnown } from './bits'
import { dayLabel } from '../lib/visits'

/**
 * WHO WORKS HERE, AND WHO IS AWAY.
 *
 * CREW-01, and deliberately about a third of it. There is no invite and that
 * is not an omission: the spec's own safeguard is that an invitation grants
 * nothing until it is accepted and that crew access is restricted server-side.
 * This product has one login and no server-side roles, so an Invite button
 * would send nothing, grant nothing, and leave a row marked Pending forever
 * that a person would reasonably read as their installer having an account.
 *
 * WHAT THE ROSTER IS ACTUALLY FOR, and it is worth more than the invite: a
 * visit can name who is going. The schedule's conflict check has said "one
 * crew is assumed, because there is no crew record to assume otherwise" since
 * the day it was written. This is the crew record, and that sentence is gone.
 *
 * A DRAFT AND A SAVE, like the business profile and the settings: a half typed
 * name is a name that ends up on a schedule, and there has to be something to
 * press that says no, wait.
 */
export default function Team({
  items,
  chromeless,
}: {
  items: ProjectSummary[] | null
  chromeless?: boolean
}) {

  const [saved, setSaved] = useState<CrewMember[]>(() => loadCrew())
  const [draft, setDraft] = useState<CrewMember[]>(() => loadCrew())
  const [said, setSaid] = useState('')

  const today = new Date().toISOString().slice(0, 10)
  const jobs = items ?? []

  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(saved),
    [draft, saved],
  )

  /*
   * HOW MUCH WORK IS ALREADY ON EACH NAME, off the visits rather than stored.
   * The spec asks for reassignment to warn on scheduled work; this is the same
   * fact said before anybody edits rather than after, which is the version
   * somebody can act on.
   */
  const booked = useMemo(() => {
    const n = new Map<string, number>()
    for (const j of jobs) {
      for (const v of j.visits ?? []) {
        if (v.doneAt) continue
        for (const id of v.assignedTo ?? []) n.set(id, (n.get(id) ?? 0) + 1)
      }
    }
    return n
  }, [jobs])

  const set = (id: string, p: Partial<CrewMember>) => {
    setDraft((list) => list.map((m) => (m.id === id ? { ...m, ...p } : m)))
    setSaid('')
  }

  const add = () => {
    setDraft((list) => [...list, newMember(`c_${Date.now().toString(36)}`)])
    setSaid('')
  }

  const save = () => {
    saveCrew(draft)
    setSaved(draft)
    setSaid('Saved.')
  }

  const addAway = (id: string) =>
    set(id, {
      away: [
        ...(draft.find((m) => m.id === id)?.away ?? []),
        { from: today, to: today, why: '' },
      ],
    })

  const setAway = (id: string, i: number, p: Partial<Away>) => {
    const m = draft.find((x) => x.id === id)
    if (!m) return
    set(id, { away: m.away.map((a, n) => (n === i ? { ...a, ...p } : a)) })
  }

  const dropAway = (id: string, i: number) => {
    const m = draft.find((x) => x.id === id)
    if (!m) return
    set(id, { away: m.away.filter((_, n) => n !== i) })
  }

  return (
    <>
      {!chromeless && (
        <div className="ws-head">
          <div>
            <h1>Team</h1>
            <p>Who works here, and who is away. Names on this list can be put on a visit.</p>
          </div>
        </div>
      )}


      <div className="setpage">
        <p className="est-answer set-safe">
          <b>Nobody on this list can sign in.</b> There is one login on this product. A name here
          is somebody a visit can be assigned to, so the week knows who is going and the schedule
          stops assuming one crew. It is not an account and it grants nothing.
        </p>

        {draft.length === 0 && (
          <div className="ws-card">
            <Empty title="Nobody on the roster yet" action={{ label: 'Add somebody', onClick: add }}>
              Add the people who go out, and their names turn up on the visit sheet.
            </Empty>
          </div>

        )}

        {draft.map((m) => {
          const off = awayOn(m, today)
          const n = booked.get(m.id) ?? 0
          return (
            <div key={m.id} className={m.active ? 'ws-card pad member' : 'ws-card pad member off'}>
              <div className="ws-form set-form">
                <label>
                  <span>Name</span>
                  <input
                    value={m.name}
                    placeholder="Their name"
                    onChange={(e) => set(m.id, { name: e.target.value })}
                  />
                </label>
                <label>
                  <span>What they do</span>
                  <input
                    value={m.role}
                    placeholder="Lead, installer, helper"
                    onChange={(e) => set(m.id, { role: e.target.value })}
                  />
                </label>
                <label className="ws-wide">
                  <span>Phone</span>
                  <input
                    value={m.phone}
                    placeholder="(555) 555-5555"
                    onChange={(e) => set(m.id, { phone: e.target.value })}
                  />
                </label>
              </div>

              {/*
                AWAY IS A RANGE OF PLAIN DATES, both ends inclusive, because a
                person who says they are away Monday to Friday means Friday.
              */}
              <div className="away">
                <p className="ws-eyebrow">Away</p>
                {m.away.length === 0 && <p className="hint">Nothing booked off.</p>}
                {m.away.map((a, i) => (
                  <div key={i} className={validAway(a) ? 'away-row' : 'away-row bad'}>
                    <input
                      type="date"
                      aria-label="Away from"
                      value={a.from}
                      onChange={(e) => setAway(m.id, i, { from: e.target.value })}
                    />
                    <span className="away-to">to</span>
                    <input
                      type="date"
                      aria-label="Away until"
                      value={a.to}
                      onChange={(e) => setAway(m.id, i, { to: e.target.value })}
                    />
                    <input
                      className="away-why"
                      aria-label="Why"
                      value={a.why}
                      placeholder="Optional"
                      onChange={(e) => setAway(m.id, i, { why: e.target.value })}
                    />
                    <button className="quiet-drop" onClick={() => dropAway(m.id, i)}>
                      Remove
                    </button>
                  </div>
                ))}
                {m.away.some((a) => !validAway(a)) && (
                  <p className="set-bad">That range runs backwards. The last day has to be on or after the first.</p>
                )}
                <button className="btn" onClick={() => addAway(m.id)}>
                  Add time off
                </button>
              </div>

              <div className="member-foot">
                <p className="member-said">
                  {off
                    ? `Away today${off.why.trim() ? `, ${off.why.trim()}` : ''}. Back after ${dayLabel(off.to)}.`
                    : 'Available today.'}
                  {n > 0 && ` ${n} ${n === 1 ? 'visit is' : 'visits are'} booked in their name.`}
                </p>
                {/*
                  OFF THE ROSTER, NOT DELETED. A visit that already names
                  somebody keeps naming them: deleting the record would empty
                  a week of assignments and nobody would know it had happened.
                */}
                <button className="quiet-drop" onClick={() => set(m.id, { active: !m.active })}>
                  {m.active ? 'Take off the roster' : 'Put back on the roster'}
                </button>
              </div>
            </div>
          )
        })}

        <div className="ws-card pad set-save">
          <div className="ws-actions">
            <button className="set-reset" onClick={add}>
              Add somebody
            </button>
            <button className="btn primary" onClick={save} disabled={!dirty}>
              Save
            </button>
            {said && <span className="ws-said good">{said}</span>}
            {!said && dirty && <span className="ws-said">Not saved yet.</span>}
          </div>
        </div>
      </div>

      <NotKnown title="What a roster does not do here" items={NOT_ON_THE_ROSTER} />
    </>
  )
}
